import * as THREE from 'three';
import React, { useMemo, useRef, useEffect, useState } from 'react';
import {
  makeMeshWithData,
  compute3DMeshDataAndPosition,
  get3DMeshDataAndPosition,
  loadTexture,
  replaceGeometryAttributesOnGeometry,
} from '../../utils';
import {
  createReactThreeGeometry,
  createReactThreeMaterial,
} from 'containers/Viewer/reactThreeUtils';
import { getMaterialData } from '../../helpers';
import * as constantsViewer from '../../constants';
import textureMirror from 'assets/images/textures/mirror2.jpg';
import textureAlu from 'assets/images/textures/alu-inox.jpg';
import textureWhiteBeech from 'assets/images/textures/whitebeech.jpg';
import textureMdfNatural from 'assets/images/textures/mdfnatural2.jpg';
import textureGlass from 'assets/images/textures/glass2.jpg';
import data from './data/drawerFrontPane';
import ThreeBSP from '../../ThreeCSG';
import { useSpring, a } from 'react-spring/three';

/*
const stateProps = createStructuredSelector({
  drawersSelected: makeSelectDrawersSelected(),
});*/

export function DrawerFrontPane({
  drawer = null,
  cabinetWidth = 1000,
  cabinetDepth = 420,
  /*level = 0,*/
  unselected = false,
}) {
  const mesh = useRef();

  /*
  const { drawersSelected } = useSelector(stateProps);

  const drawerSelected =
    !unselected &&
    Array.isArray(drawersSelected) &&
    drawersSelected.includes(level);
    */

  const [prevSizeData, setPrevSizeData] = useState({
    cabinetWidth: cabinetWidth,
    drawerHeight: drawer.height,
  });

  const textures = useMemo(
    () => ({
      textureWhiteBeechLoaded: loadTexture(textureWhiteBeech),
      textureMdfNaturalLoaded: loadTexture(textureMdfNatural),
      textureMirrorLoaded: loadTexture(textureMirror),
      textureAluLoaded: loadTexture(textureAlu),
      textureGlassLoaded: loadTexture(textureGlass),
    }),
    [
      textureWhiteBeech,
      textureMdfNatural,
      textureMirror,
      textureAlu,
      textureGlass,
    ],
  );

  const baseDataModel = useMemo(
    () => compute3DMeshDataAndPosition(data.drawerFrontPane),
    [data.drawerFrontPane],
  );

  const baseData = useMemo(
    () =>
      get3DMeshDataAndPosition(
        generateGeometryFrontPaneThree(
          drawer,
          prevSizeData.drawerHeight,
          baseDataModel.size,
          baseDataModel.size,
          prevSizeData.cabinetWidth,
          drawer.drawerHandlesType,
        ),
      ),
    [data.drawerFrontPane],
  );

  const [spring, set] = useSpring(() => ({
    position: getPosition(baseData.position, cabinetDepth),
    scale: [1, 1, 1],
    immediate: true,
    config: constantsViewer.REACT_SPRING_ANIMATIONS_CONF,
  }));

  const materialData = getMaterialData(drawer, textures);

  useEffect(
    () => {
      let self = mesh.current;
      if (self)
        changeGeometryWithEcoAndEcoPlus(
          self,
          drawer,
          prevSizeData.drawerHeight,
          baseDataModel.size,
          baseDataModel.size,
          prevSizeData.cabinetWidth,
          drawer.drawerHandlesType,
        );
    },
    [drawer.drawerHandlesType],
  );

  useEffect(
    () => {
      let self = mesh.current;
      if (
        self &&
        (cabinetWidth != prevSizeData.cabinetWidth ||
          drawer.height != prevSizeData.drawerHeight)
      ) {
        self.geometry.computeBoundingBox();
        let size = new THREE.Vector3();
        self.geometry.boundingBox.getSize(size);

        if (
          drawer.drawerHandlesType === 'E' ||
          drawer.drawerHandlesType === 'EP'
        ) {
          // Change geometry to remove hole (null for hole) on old size values,
          // only if animations are active
          if (constantsViewer.REACT_SPRING_ANIMATIONS_ACTIVE)
            changeGeometryWithEcoAndEcoPlus(
              self,
              drawer,
              prevSizeData.drawerHeight,
              baseDataModel.size,
              baseDataModel.size,
              prevSizeData.cabinetWidth,
              null,
            );

          // Animate scaling and after update geometry to add hole
          set({
            scale: getScale(
              baseDataModel.size,
              size,
              cabinetWidth,
              drawer.height,
            ),
            onRest: () => {
              changeGeometryWithEcoAndEcoPlus(
                self,
                drawer,
                drawer.height,
                baseDataModel.size,
                baseDataModel.size,
                cabinetWidth,
                drawer.drawerHandlesType,
              );
            },
          });
        } else {
          set({
            scale: getScale(
              baseDataModel.size,
              size,
              cabinetWidth,
              drawer.height,
            ),
            onRest: null,
          });
        }

        setPrevSizeData({
          cabinetWidth: cabinetWidth,
          drawerHeight: drawer.height,
        });
      }
    },
    [cabinetWidth, drawer.height],
  );

  useEffect(
    () => {
      let self = mesh.current;
      if (self)
        set({
          position: getPosition(baseData.position, cabinetDepth),
          onRest: null,
        });
    },
    [cabinetDepth],
  );

  return (
    <a.mesh
      ref={mesh}
      renderOrder={constantsViewer.ZORDER_DOOR_HOLE_SHADOW}
      receiveShadow
      castShadow
      position={spring.position}
      scale={spring.scale}
    >
      {createReactThreeGeometry(baseData)}
      {createReactThreeMaterial(materialData, unselected)}
    </a.mesh>
  );
}

export function DrawerFrontBottomShadow({
  drawer = null,
  cabinetWidth = 1000,
  cabinetDepth = 420,
  unselected = false,
}) {
  const mesh = useRef();

  const baseData = useMemo(
    () => compute3DMeshDataAndPosition(data.drawerFrontPane),
    [data.drawerFrontPane],
  );

  const [spring, set] = useSpring(() => ({
    position: getPositionSimulationSeparation(
      baseData.position,
      cabinetDepth,
      drawer.height,
    ),
    scale: getScaleSimulationSeparation(
      baseData.size,
      baseData.size,
      cabinetWidth,
    ),
    immediate: true,
    config: constantsViewer.REACT_SPRING_ANIMATIONS_CONF,
  }));

  useEffect(
    () => {
      let self = mesh.current;
      if (self) {
        self.geometry.computeBoundingBox();
        let size = new THREE.Vector3();
        self.geometry.boundingBox.getSize(size);

        set({
          scale: getScaleSimulationSeparation(
            baseData.size,
            size,
            cabinetWidth,
          ),
          position: getPositionSimulationSeparation(
            baseData.position,
            cabinetDepth,
            drawer.height,
          ),
        });
      }
    },
    [cabinetWidth, drawer.height, cabinetDepth],
  );

  return (
    <a.mesh
      ref={mesh}
      receiveShadow
      castShadow
      renderOrder={constantsViewer.ZORDER_DOORS}
      position={spring.position}
      scale={spring.scale}
    >
      {createReactThreeGeometry(baseData)}
      <meshPhongMaterial
        attach="material"
        color={0x000000}
        specular={0x919191}
        shininess={4.92458}
        opacity={unselected ? 0 : 0.5}
        transparent
      />
    </a.mesh>
  );
}

function getPosition(basePosition, cabinetDepth) {
  const cabinetDiffDepth = cabinetDepth - constantsViewer.MODEL_CABINET_DEPTH;
  return [
    basePosition.x,
    basePosition.y,
    basePosition.z + cabinetDiffDepth / 2,
  ];
}

function getPositionSimulationSeparation(
  basePosition,
  cabinetDepth,
  drawerHeight,
) {
  const cabinetDiffDepth = cabinetDepth - constantsViewer.MODEL_CABINET_DEPTH;
  return [
    basePosition.x,
    basePosition.y - drawerHeight / 2 - 1,
    basePosition.z + cabinetDiffDepth / 2,
  ];
}

/**
 * Get scale multiplicator from a drawer size to a goal drawer size
 *
 * baseSize: real size of model drawer
 * size: real size of goal drawer
 * cabinetWidth: width of goal cabinet (not exactly same as drawer)
 * drawerHeight: height of goal drawer (not exactly same as drawer, here is "commercial" height)
 */
function getScale(baseSize, size, cabinetWidth, drawerHeight) {
  let spaceWidthNoScale = constantsViewer.MODEL_CABINET_WIDTH - baseSize.x;
  let spaceHeightNoScale = constantsViewer.MODEL_DRAWER_HEIGHT - baseSize.y;

  return [
    (cabinetWidth - spaceWidthNoScale) / size.x,
    (drawerHeight - spaceHeightNoScale) / size.y,
    1,
  ];
}

function getScaleSimulationSeparation(baseSize, size, cabinetWidth) {
  let spaceWidthNoScale = constantsViewer.MODEL_CABINET_WIDTH - baseSize.x;

  return [
    (cabinetWidth - spaceWidthNoScale) / size.x,
    2 / constantsViewer.MODEL_DRAWER_HEIGHT,
    1,
  ];
}

function changeGeometryWithEcoAndEcoPlus(
  self,
  drawer,
  drawerHeight,
  baseSize,
  size,
  cabinetWidth,
  drawerHandlesType,
) {
  let newData = get3DMeshDataAndPosition(
    generateGeometryFrontPaneThree(
      drawer,
      drawerHeight,
      baseSize,
      size,
      cabinetWidth,
      drawerHandlesType,
    ),
  );

  replaceGeometryAttributesOnGeometry(self.geometry, newData);

  // Revert scaling on geometry because mesh is already scaled
  // and cannott change scale on mesh beacause that causes visible bad change of scale
  self.geometry.scale(1 / self.scale.x, 1 / self.scale.y, 1);
}

function generateCylinderMesh(position) {
  const cylinderRadius = 85;
  var geometryCylinder = new THREE.CylinderGeometry(
    cylinderRadius,
    cylinderRadius,
    40,
    32,
  );
  var materialCylinder = new THREE.MeshBasicMaterial({ color: 0x000000 });
  var cylinder = new THREE.Mesh(geometryCylinder, materialCylinder);
  cylinder.rotation.set(0, Math.PI / 2, Math.PI / 2);
  cylinder.position.set(position.x, position.y, position.z);

  return cylinder;
}

function generateGeometryFrontPaneThree(
  drawer,
  drawerHeight,
  baseSize,
  size,
  cabinetWidth,
  drawerHandlesType,
  hd = false,
) {
  let modelCabinetWidth = constantsViewer.MODEL_CABINET_WIDTH;
  let modelDrawerHeight = constantsViewer.MODEL_DRAWER_HEIGHT;
  let cabinetDiffWith = cabinetWidth - modelCabinetWidth;

  let scale = getScale(baseSize, size, cabinetWidth, drawerHeight);
  let scaleX = scale[0];
  let scaleY = scale[1];

  let paneMesh = makeMeshWithData(data.drawerFrontPane);

  // ThreeCSG does not support adding texture on Mesh with BufferGeometry, so need to
  // convert to Geometry (no impact on rendering performance due to final export in BufferGeometry)
  var geometry = new THREE.Geometry().fromBufferGeometry(paneMesh.geometry);
  paneMesh = new THREE.Mesh(geometry, paneMesh.material);

  // Reset position properly
  var center = new THREE.Vector3();
  paneMesh.geometry.computeBoundingBox();
  paneMesh.geometry.boundingBox.getCenter(center);
  paneMesh.geometry.center();
  paneMesh.position.copy(center);

  paneMesh.geometry.scale(scaleX, scaleY, 1);

  var bsp = new ThreeBSP(paneMesh);

  var holeMesh;
  let xOffset = 0;

  switch (drawerHandlesType) {
    case 'E':
      holeMesh = makeMeshWithData(data.ecoHoleLeft);
      // Reset position properly
      center = new THREE.Vector3();
      holeMesh.geometry.computeBoundingBox();
      holeMesh.geometry.boundingBox.getCenter(center);
      holeMesh.geometry.center();
      holeMesh.position.copy(center);
      holeMesh.position.z -= 97;
      holeMesh.position.y += 40;
      xOffset = 208;
      break;
    case 'EP':
      holeMesh = makeMeshWithData(data.ecoPlusHoleLeft);
      // Reset position properly
      center = new THREE.Vector3();
      holeMesh.geometry.computeBoundingBox();
      holeMesh.geometry.boundingBox.getCenter(center);
      holeMesh.geometry.center();
      holeMesh.position.copy(center);
      holeMesh.position.z -= 135;
      xOffset = 252;
      break;
    default:
      return bsp.toBufferGeometry();
  }

  holeMesh.position.y -= (modelDrawerHeight - drawerHeight) / 2;

  if (drawer.numberOfHandles === 2) {
    var holeMeshRight = holeMesh.clone();

    holeMesh.position.x -= cabinetDiffWith / 2;
    if (drawerHandlesType === 'E') {
      holeMeshRight.position.x -= cabinetDiffWith / 2;
      holeMesh.position.x -= xOffset;
      holeMeshRight.position.x -= xOffset;
      holeMesh.position.x += cabinetWidth / 2;
      holeMeshRight.position.x += cabinetWidth / 2;
      holeMesh.position.x -= 195;
      holeMeshRight.position.x += 195;

      if (!hd) {
        holeMesh = generateCylinderMesh(holeMesh.position);
        holeMeshRight = generateCylinderMesh(holeMeshRight.position);
      }
    } else {
      holeMeshRight.position.x += modelCabinetWidth + cabinetDiffWith / 2;
      holeMeshRight.position.x -= 2 * xOffset;
    }

    return bsp
      .subtract(new ThreeBSP(holeMesh))
      .subtract(new ThreeBSP(holeMeshRight))
      .toBufferGeometry();
  }

  holeMesh.position.x -= cabinetDiffWith / 2;
  holeMesh.position.x -= xOffset;
  holeMesh.position.x += cabinetWidth / 2;

  if (!hd && drawer.drawerHandlesType === 'E')
    holeMesh = generateCylinderMesh(holeMesh.position);

  return bsp.subtract(new ThreeBSP(holeMesh)).toBufferGeometry();
}
